import { useFragment } from "@/generated";
import { withToast } from "@/lib/utils/withToast";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  DeleteLessonsDocument,
  type LessonFieldsFragment,
  LessonFieldsFragmentDoc,
  type LinkedCopy,
  SlideFieldsFragmentDoc,
} from "../../../generated/graphql";
import { updateDeletedSlidesInCache } from "../slides/useDeleteSlides";

export function updateDeletedLessonsInCache(
  cache: ApolloCache,
  deletedLessons: LessonFieldsFragment[]
) {
  const parentId = deletedLessons[0]?.parentId;
  cache.batch({
    update(cache) {
      for (const lesson of deletedLessons) {
        if (!lesson.isDeleted) return;
        const id = cache.identify(lesson);

        if (id) {
          cache.evict({ id });
        }

        // Remove from original item's linkedCopies if this is a linked copy
        if (lesson.isLinkedCopy && lesson.originalLessonId) {
          const linkedId = cache.identify({ __typename: "LinkedCopy", id: lesson.id });

          if (linkedId) {
            cache.evict({ id: linkedId });
          }
        }
      }

      if (parentId === null) {
        cache.modify({
          id: "ROOT_QUERY",
          fields: {
            rootTrashCount(existingData) {
              return existingData - deletedLessons.filter((lesson) => lesson.isDeleted).length;
            },
          },
        });
      } else {
        cache.modify({
          id: `Project:${parentId}`,
          fields: {
            trashCount(existingData = 0) {
              return existingData - deletedLessons.filter((lesson) => lesson.isDeleted).length;
            },
          },
        });
      }
    },
  });
}

/**
 * Hook for soft deleting lessons and their slides.
 *
 * @example
 * const { deleteLessons, loading, error } = useDeleteLessons();
 * // Delete single lesson
 * await deleteLessons(["lesson123"]);
 * // Delete multiple lessons
 * await deleteLessons(["lesson1", "lesson2", "lesson3"]);
 */
export function useDeleteLessons() {
  const [deleteLessonsMutation, { loading, error }] = useMutation(DeleteLessonsDocument, {
    update(cache, { data }) {
      if (!data?.deleteLessons) return;

      const lessons = data?.deleteLessons.lessons.map((lesson) =>
        useFragment(LessonFieldsFragmentDoc, lesson)
      );

      const slides = data?.deleteLessons.slides.map((slide) =>
        useFragment(SlideFieldsFragmentDoc, slide)
      );

      updateDeletedSlidesInCache(cache, slides);

      try {
        updateDeletedLessonsInCache(cache, lessons);
        cache.gc();
      } catch (error) {
        console.error("Delete lessons cache update failed:", error);
      }
    },
  });

  const deleteLessons = async (ids: string | string[], skipCheck: boolean) => {
    const idsArray = Array.isArray(ids) ? ids : [ids];
    return withToast(
      async () => {
        const result = await deleteLessonsMutation({ variables: { ids: idsArray, skipCheck } });

        const maskedLessons = result.data?.deleteLessons.lessons ?? [];
        const deletedLessons = maskedLessons.map((lesson) =>
          useFragment(LessonFieldsFragmentDoc, lesson)
        );

        return deletedLessons;
      },
      {
        loading: `Deleting ${idsArray.length} lesson${idsArray.length !== 1 ? "s" : ""}...`,
        success: `Successfully deleted ${idsArray.length} lesson${idsArray.length !== 1 ? "s" : ""}`,
        error: "Failed to delete lessons. Please try again.",
      },
      "deleteLessons"
    );
  };

  return { deleteLessons, loading, error };
}
